import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { ButtonDeleteIds } from 'components/ButtonDeleteIds';
import { TableTopPanel } from 'components/TableTopPanel';
import type { RequestStatus } from 'features/request/requestSlice';

import type { ColDef, ColGroupDef, IDatasource, RowClassRules } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import type { MutableRefObject } from 'react';
import { useImperativeHandle, useRef } from 'react';

import Style from './AgreementPriceListTable.module.css';

interface AgreementPriceListTableProps<T> {
    rowCount: number;
    totalCount: number;
    columnsForTableSettings: (ColDef<T> | ColGroupDef<T>)[];
    handleTableSettingsConfirm: (value: Record<string, boolean>) => void;
    isColumnVisible: (id: string) => boolean;
    datasource: IDatasource;
    columns: (ColDef<T> | ColGroupDef<T>)[];
    requestStatus: RequestStatus;
    removeRowsData: any;
    removeRowsStatus: RequestStatus;
    removeRowsError: any;
    removeRowsSetData: () => void;
    handleRemoveRows: () => void;
    checkedIdsArr?: number[];
    handleClearChecked?: () => void;
    refreshCurrentTableRef: MutableRefObject<AgGridReact | null | undefined>;
    tableContext: any;
    emptyDataComponent: () => string;
    hasItemRow: boolean;
    rowClassRules?: RowClassRules;
}

export const AgreementPriceListTable = <T = any,>({
    rowCount,
    totalCount,
    columnsForTableSettings,
    handleTableSettingsConfirm,
    isColumnVisible,
    datasource,
    columns,
    requestStatus,
    checkedIdsArr,
    handleClearChecked,
    removeRowsData,
    removeRowsStatus,
    removeRowsError,
    removeRowsSetData,
    handleRemoveRows,
    refreshCurrentTableRef,
    tableContext,
    emptyDataComponent,
    hasItemRow,
    rowClassRules,
}: AgreementPriceListTableProps<T>) => {
    const tableRef = useRef<AgGridReact>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useImperativeHandle(refreshCurrentTableRef, () => tableRef?.current as AgGridReact, [hasItemRow]);

    return hasItemRow ? (
        <>
            <TableTopPanel
                checkedRowsArr={checkedIdsArr}
                resetCheckedRows={handleClearChecked}
                totalItems={totalCount}
                fetchedItems={rowCount}
                optionalColumns={columnsForTableSettings}
                handleTableSettingsConfirm={handleTableSettingsConfirm}
                isColumnVisible={isColumnVisible}
                showPinColumn
                areChildrenOnLeftSide
                style={{ marginTop: 10 }}
                tableApi={tableRef?.current}
                ref={panelRef}
            >
                <ButtonDeleteIds
                    deleteData={removeRowsData}
                    deleteStatus={removeRowsStatus}
                    deleteError={removeRowsError}
                    deleteSetData={removeRowsSetData}
                    handleDelete={handleRemoveRows}
                    style={{ marginRight: '16px' }}
                />
            </TableTopPanel>

            <div
                style={{
                    height: `calc(100vh - ${(panelRef.current?.getBoundingClientRect?.().bottom ?? 0) + 35}px)`,
                }}
            >
                <AgGridTable
                    ref={tableRef}
                    datasource={datasource}
                    columnDefs={columns}
                    maxConcurrentDatasourceRequests={1}
                    maxBlocksInCache={50}
                    cacheBlockSize={50}
                    rowModelType="infinite"
                    context={tableContext}
                    rowClassRules={rowClassRules}
                />
            </div>
        </>
    ) : (
        <div className={Style.customOverlay}>{emptyDataComponent()}</div>
    );
};

AgreementPriceListTable.displayName = 'AgreementPriceListTable';
